import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import * as Rx from 'rxjs';
import { tap, map, switchMap } from 'rxjs/operators';
import { AppSettingService } from '../app.service';
import { UserInfoService} from './user-info.service';
import { MapAPIService } from '../map/map-api.service';

interface ISaveUser {
	Role: string;
	PersonId?: number;
	FirstName: string;
	LastName: string;
	Email?: string;
	BirthDate?: string;
	UserName?: string;
	Password?: string;
	SelectedSchool: any;
	Schools: { SchoolId: number, SchoolName: string, City: string }[]
};

export class AddTeacher implements ISaveUser {
	Role = "Teacher";
	constructor(
		public FirstName: string,
		public LastName: string,
		public Email: string,
		public SelectedSchool: any,
		public Schools: { SchoolId: number, SchoolName: string, City: string }[]
	) {
		this.Role = "Teacher";
	}
	public reset() {
		this.FirstName = "";
		this.LastName = "";
		this.Email = "";
		this.SelectedSchool = null;
		this.Schools = null;
	}
}

export class EditTeacher implements ISaveUser {
	Role = "Teacher";
	constructor(
		public PersonId: number,
		public FirstName: string,
		public LastName: string,
		public Email: string,
		public SelectedSchool: any,
		public Schools: { SchoolId: number, SchoolName: string, City: string }[]
	) { }

	public reset() {
		this.PersonId = null;
		this.FirstName = "";
		this.LastName = "";
		this.Email = "";
		this.SelectedSchool = null;
		this.Schools = null;
	}
}

export class AddStudent implements ISaveUser {
	Role = "Student";
	constructor(
		public FirstName: string,
		public LastName: string,
		public BirthDate: string,
		public UserName: string,
		public Password: string,
		public SelectedSchool: any,
		public Schools: { SchoolId: number, SchoolName: string, City: string }[]
	) { }

	public reset() {
		this.FirstName = "";
		this.LastName = "";
		this.BirthDate = "",
		this.UserName = "",
		this.Password = "",
		this.SelectedSchool = null;
		this.Schools = null;
	}
}

export class EditStudent implements ISaveUser {
	Role = "Student";
	constructor(
		public PersonId: number,
		public FirstName: string,
		public LastName: string,
		public BirthDate: string,
		public UserName: string,
		public Password: string,
		public SelectedSchool: any,
		public Schools: { SchoolId: number, SchoolName: string, City: string }[]
	) { }

	public reset() {
		this.PersonId = null;
		this.FirstName = "";
		this.LastName = "";
		this.BirthDate = "",
		this.UserName = "",
		this.Password = "",
		this.SelectedSchool = null;
		this.Schools = null;
	}
}

export interface IAnonymousUser {
	Email: string,
	EmailStatus?: boolean,
	InstitutionCode: string
};

export class NewAnonymousUser implements IAnonymousUser {
	constructor() { }
	Email = "";
	EmailStatus = null as any;
	InstitutionCode = "";
}


@Injectable({
	providedIn: 'root'
})
export class UserAPIService {
	constructor(
		private userInfo: UserInfoService,
		private http: HttpClient
	) { }

	private url = AppSettingService.appConfig.JourneyAPIUrl + "User";

	adminSearch(role:string, schoolId:number, kw: string, limit: number, offset: number): Rx.Observable<any> {
		kw = kw.trim();
		return this.http.get<any>(
			this.url + '/Admin?role=' + role + '&schoolId=' + (schoolId==null?'':encodeURIComponent(schoolId)) + '&kw=' + encodeURIComponent(kw) + '&limit=' + limit.toString() + '&offset=' + offset.toString(),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	adminGet(personId: number): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + "/Admin/" + personId.toString(),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	adminSave(user: ISaveUser): Rx.Observable<any> {
		return this.http.post<any>(
			this.url + "/Admin",
			user,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	register(reg:any): Rx.Observable<any> {
		return this.http.post<any>(
			this.url + "/Signup",
			reg
		);
	}

	globalRegister(reg: any): Rx.Observable<any> {
		return this.http.post<any>(
			this.url + "/GlobalSignup",
			reg
		);
	}

	updateGlobalAccountReqiretCount(): Rx.Observable<any> {
		return this.http.put<any>(
			this.url + "/GlobalSignup/Require",
			null,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		)
	}

	getGlobalAccountReqiretCount(): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + "/GlobalSignup/Require",
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		)
	}

	sendEmailVerification(email: string): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + "/VerifyEmail/Send?email=" + encodeURIComponent(email),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	confirmEmailVerification(email: string, code:string): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + "/VerifyEmail/Confirm?email=" + encodeURIComponent(email) + "&code=" + encodeURIComponent(code),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	checkUserNameExist(username: string, zoneName: string): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + "/CheckUserNameExist?username=" + encodeURIComponent(username) + "&zone=" + encodeURIComponent(zoneName)
		);
	}

	checkValidationCodeExist(code:string): Rx.Observable<any> {
		return this.http.get<any>(
			this.url  + '/CheckValidationCodeExist?code=' + encodeURIComponent(code)
		);
	}

	resetPassword(req:any): Rx.Observable<any> {
		return this.http.post<any>(
			this.url + "/ResetPassword",
			req
		);
	}

	// Anonymous
	saveAnonymousUser(data: IAnonymousUser): Rx.Observable<any> {
		return this.http.post<any>(
			this.url + "/AnonymousUser",
			data,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	};

	getAnonymousUser(id: string): Rx.Observable<any> {
		return this.http.get<any>(
			this.url +  "/AnonymousUser/" + encodeURIComponent(id),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	transferAnonymousUserData (anonymousUserLink: string): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + '/AnonymousUser/' + encodeURIComponent(anonymousUserLink) + '/TransferData',
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	copyAnonymousSessionData(sessionId: string): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + '/AnonymousUser/' + encodeURIComponent(sessionId) + '/CopySessionData',
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	//admin
	getUserProfile(userId: string): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + "/UserProfile" + (userId != null && userId.length > 0 ? "?UserId=" + userId : ""),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}
}


import { IClassStudentPicker } from '../class/enrollment.service';
@Injectable({
	providedIn: 'root'
})
export class ActivityAPIService {
	constructor(
		private userInfo: UserInfoService,
		private http: HttpClient,
		private mapAPI: MapAPIService
	) { }

	private url = AppSettingService.appConfig.JourneyAPIUrl + "Activity";

	userInstitutionsVisited(userId: string, offset: number, limit: number): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + '/Institution?userId=' + encodeURIComponent(userId) + '&offset= ' + offset.toString() + '&limit=' + limit.toString(),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getActivityTotal(userId: string, activityType: number, objectType: number, depth: number): Rx.Observable<any> {
		var link = "";
		if (depth == null) {
			link = this.url + '/TotalCount?userId=' + encodeURIComponent(userId) + '&activityType= ' + encodeURIComponent(activityType) + '&objectType=' + encodeURIComponent(objectType);
		}
		else {
			link = this.url + '/TotalCount?userId=' + encodeURIComponent(userId) + '&activityType= ' + encodeURIComponent(activityType) + '&objectType=' + encodeURIComponent(objectType) + '&depth=' + encodeURIComponent(depth);
		}
		return this.http.get<any>(
			link,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getSummary(filter: IClassStudentPicker): Rx.Observable<any> {
		return this.http.post<any>(
			this.url + "/Summary",
			filter,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getStats(filter: IClassStudentPicker): Rx.Observable<any> {
		return this.http.post<any>(
			this.url + "/Stats",
			filter,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	userOccupationMetrics(userId:string): Rx.Observable<any> {
		return this.mapAPI.getUserMap().pipe(switchMap(
			(response) => {
				return this.http.get<any>(
					this.url + '/Career/Metrics?userGuid=' + encodeURIComponent(userId) + '&careerVersion=' + encodeURIComponent(response.CareerVersion.Version),
					{
						withCredentials: true,
						headers: { 'Authorization': this.userInfo.authHeader(false) }
					}
				);
			}
		));
	}

	userOccupationVisited(userId: string, offset:number, limit:number): Rx.Observable<any> {
		return this.mapAPI.getUserMap().pipe(switchMap(
			response => {
				return this.http.get<any>(
					this.url + '/Career/Visited?userGuid=' + encodeURIComponent(userId) + '&careerVersion=' + encodeURIComponent(response.CareerVersion.Version) + '&offset=' + offset + '&limit=' + limit,
					{
						withCredentials: true,
						headers: { 'Authorization': this.userInfo.authHeader(false) }
					}
				);
			}
		));
	}

	log(objectType: string, activitytype: string, objectId: string, note: string, associatedObjectId: string): Rx.Observable<any> {
		var data = {
			"ActivityType": activitytype,
			"AssociatedObjectId": associatedObjectId,
			"Note": note,
			"ObjectId": objectId,
			"ObjectType": objectType,
			"InstitutionCode": this.userInfo.getInstitutionCode()
		};
		return this.http.post<any>(
			this.url + '/Log',
			data,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getUserActivityStats(userGuid: string): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + '/UserActivityStats/' + encodeURIComponent(userGuid),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}
}


@Injectable({
	providedIn: 'root'
})
export class SessionAPIService {
	constructor(
		private userInfo: UserInfoService,
		private http: HttpClient
	) { }

	private url = AppSettingService.appConfig.JourneyAPIUrl + "Session";

	getSessionId(): Rx.Observable<string> {
		return this.http.get<any>(
			AppSettingService.appConfig.JourneyAPIUrl + "Session/SessionId"
		).pipe(map(
			response => {
				return response.Detail;
			}
		));
	}

	signOn(username: string, password: string, zone: string): Rx.Observable<any> {
		zone = (zone ?? "").trim();
		this.userInfo.clearStorage();
		return this.http.get<any>(
			this.url + "/SignOn?UserName=" + encodeURIComponent(username) + "&Password=" + encodeURIComponent(password) + "&zone=" + encodeURIComponent(zone)
		);
	}

	signOnAsOther(otherPersonId: string): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + "/SignOnAsOther/" + encodeURIComponent(otherPersonId),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	globalLogin(username: string, password: string): Rx.Observable<any> {
		this.userInfo.clearStorage();
		return this.http.get<any>(
			this.url + "/GlobalSignOn?UserName=" + encodeURIComponent(username) + "&Password=" + encodeURIComponent(password),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	// get the cookie for anonymous user. We need to delay to let browser has chance to set the cookie
	anonymous(): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + '/Anonymous'
		);
	}

	getSessionInfo(): Rx.Observable<any> {
		return this.http.get<any>(
			this.url,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	loginReport = {
		lastLogin: (userId: string): Rx.Observable<any> => {
			return this.http.get<any>(
				this.url + "/LoginReport/LastLogin?userGuid=" + userId,
				{
					withCredentials: true,
					headers: { 'Authorization': this.userInfo.authHeader(false) }
				}
			);
		},
		month: (userId: string, month: number, year: number): Rx.Observable<any> => {
			return this.http.get<any>(
				this.url + "/LoginReport/Month?userGuid=" + userId + "&month=" + month.toString() + "&year=" + year.toString(),
				{
					withCredentials: true,
					headers: { 'Authorization': this.userInfo.authHeader(false) }
				}
			);
		},
		day: (userId: string, month: number, year: number): Rx.Observable<any> => {
			return this.http.get<any>(
				this.url + "/LoginReport/Day?userGuid=" + userId + "&month=" + month.toString() + "&year=" + year.toString(),
				{
					withCredentials: true,
					headers: { 'Authorization': this.userInfo.authHeader(false) }
				}
			);
		},
		hour: (userId: string, month: number, day: number, year: number): Rx.Observable<any> => {
			return this.http.get<any>(
				this.url + "/LoginReport/Hour?userGuid=" + userId + "&month=" + month.toString() + "&year=" + year.toString() + "&dayNum=" + day,
				{
					withCredentials: true,
					headers: { 'Authorization': this.userInfo.authHeader(false) }
				}
			);
		}
	}
}


@Injectable({
	providedIn: 'root'
})
export class WebAPIService {
	constructor(
		private http: HttpClient,
		private userInfo: UserInfoService
	) { }

	private url = AppSettingService.appConfig.WebAPIUrl;

	signOn(): Rx.Observable<any> {
		this.userInfo.clearStorage();
		return this.http.get<any>(
			this.url + "Session/SignOn",
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	signOff(): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + "Session/SignOff"
		);
	}
}

